import { Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { Calendar, Gauge, Fuel, MapPin, Star, Car } from 'lucide-react';
import { Badge } from '../components/ui/badge';

export default function ListingCard({ listing }) {
  const { t } = useLang();
  const isFeatured = listing.is_featured && listing.featured_until && new Date(listing.featured_until) > new Date();

  return (
    <Link
      to={`/listings/${listing.id}`}
      className={`bg-[#141B2D] rounded-xl border overflow-hidden hover:-translate-y-1 transition-all duration-300 hover:shadow-xl hover:shadow-[#FADB5F]/5 flex flex-col ${isFeatured ? 'border-[#FADB5F]/50 ring-1 ring-[#FADB5F]/20' : 'border-[#1E293B]'}`}
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-[#1E293B]">
        {listing.images?.length > 0 ? (
          <img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center ${listing.images?.length > 0 ? 'hidden' : 'flex'}`}>
          <div className="text-center">
            <Car className="w-8 h-8 text-[#475569] mx-auto mb-1" />
            <span className="text-xs text-[#475569]">No photo</span>
          </div>
        </div>
        {isFeatured && (
          <div className="absolute top-2 left-2" data-testid="featured-badge">
            <Badge className="bg-[#FADB5F]/90 text-[#856505] text-[10px] font-bold px-2 py-0.5 tracking-wider border-0 backdrop-blur-sm">
              <Star className="w-3 h-3 mr-1 fill-[#856505]" />
              {t('listing.featured')}
            </Badge>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <Badge className="bg-[#0B0F19]/80 text-[#F1F5F9] text-[10px] font-bold px-2 py-0.5 tracking-wider border-0 backdrop-blur-sm uppercase">
            {t(`categories.${listing.vehicle_type}`) || listing.vehicle_type}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-sm text-[#F1F5F9] line-clamp-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {listing.title}
          </h3>
        </div>

        <p className="text-lg font-bold text-[#FADB5F] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {listing.price?.toLocaleString()} {listing.currency || 'EUR'}
        </p>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-1.5 text-xs text-[#94A3B8] mb-3">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#475569]" />
            <span>{listing.year}</span>
          </div>
          {listing.mileage != null && (
            <div className="flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5 text-[#475569]" />
              <span>{listing.mileage?.toLocaleString()} {t('listing.km')}</span>
            </div>
          )}
          {listing.fuel_type && (
            <div className="flex items-center gap-1.5">
              <Fuel className="w-3.5 h-3.5 text-[#475569]" />
              <span>{t(`fuel_types.${listing.fuel_type}`) || listing.fuel_type}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-[#475569]" />
            <span className="truncate">{listing.city}, {listing.country}</span>
          </div>
        </div>

        {/* Brand/Model */}
        <div className="mt-auto pt-2 border-t border-[#1E293B] flex items-center justify-between">
          <span className="text-xs font-medium text-[#94A3B8]">{listing.brand} {listing.model}</span>
          {listing.power_hp && (
            <span className="text-xs text-[#475569]">{listing.power_hp} {t('listing.hp')}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
